import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';

const PROVIDER_INFO = {
  grok:       { label: 'Grok (xAI)',  base: 'https://api.x.ai/v1',         models: ['grok-4.3', 'grok-4.20-0309-non-reasoning', 'grok-4.20-0309-reasoning', 'grok-4.20-multi-agent-0309'] },
  openai:     { label: 'OpenAI',      base: 'https://api.openai.com/v1',   models: ['gpt-4o', 'gpt-4o-mini', 'gpt-4-turbo'] },
  anthropic:  { label: 'Anthropic',   base: 'https://api.anthropic.com/v1', models: ['claude-sonnet-4-6', 'claude-haiku-4-5', 'claude-opus-4-7'] },
  gemini:     { label: 'Google Gemini', base: 'https://generativelanguage.googleapis.com/v1beta', models: ['gemini-3.5-flash', 'gemini-3.1-flash-lite', 'gemini-3-pro-preview', 'gemini-flash-latest', 'gemini-2.5-flash'] },
  openrouter: { label: 'OpenRouter (multi)', base: 'https://openrouter.ai/api/v1', models: ['meta-llama/llama-3.3-70b-instruct', 'anthropic/claude-3.5-sonnet', 'openai/gpt-4o', 'google/gemini-2.0-flash-exp:free'] },
};

const TASKS = ['intent_classifier', 'message_generator', 'intent_summary', 'modify_template', 'categorize'];

export default function AIConfigPage() {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [testTask, setTestTask] = useState('message_generator');

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/ai-config');
      setCfg(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Błąd'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const updateProvider = async (prov, patch) => {
    try {
      await api.put('/ai-config', { providers: { [prov]: patch } });
      toast.success(`${PROVIDER_INFO[prov].label} zaktualizowany`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Błąd'); }
  };

  const updateTask = async (task, patch) => {
    try {
      await api.put('/ai-config', { tasks: { [task]: patch } });
      toast.success(`Task ${task} zaktualizowany`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Błąd'); }
  };

  const setDefault = async (provider, model) => {
    try {
      await api.put('/ai-config', { default_provider: provider, default_model: model });
      toast.success(`Default: ${provider}/${model}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Błąd'); }
  };

  const runTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const r = await api.post('/ai-config/test', { task: testTask, user: 'Powiedz krotko po polsku: dzialasz?' });
      setTestResult(r.data);
    } catch (e) {
      setTestResult({ ok: false, reason: e.response?.data?.detail || 'Błąd' });
    } finally { setTesting(false); }
  };

  if (loading || !cfg) return <div className="p-6"><Skeleton className="h-32" /></div>;

  return (
    <div className="p-6 space-y-4">
      <div>
        <h1 className="text-2xl font-heading font-semibold">Konfiguracja AI providerów</h1>
        <p className="text-sm text-muted-foreground">Wsparcie: Grok (xAI), OpenAI, Anthropic. emergent.sh — zablokowane (porzucone).</p>
      </div>

      {/* PROVIDERY */}
      {Object.entries(PROVIDER_INFO).map(([prov, info]) => {
        const p = cfg.providers?.[prov] || {};
        return (
          <Card key={prov}>
            <CardContent className="p-4 space-y-3">
              <div className="flex justify-between items-center">
                <div>
                  <h3 className="font-semibold flex items-center gap-2">
                    {info.label}
                    {p.enabled && p.api_key_set ? (
                      <Badge variant="default" className="text-[10px]">aktywny</Badge>
                    ) : (
                      <Badge variant="outline" className="text-[10px] opacity-50">nieaktywny</Badge>
                    )}
                  </h3>
                  <p className="text-xs text-muted-foreground">{info.base}</p>
                </div>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={!!p.enabled} onChange={(e) => updateProvider(prov, { enabled: e.target.checked })} />
                  enabled
                </label>
              </div>
              <div>
                <Label>API key {p.api_key_set ? <span className="text-xs opacity-60">(ustawiony: {p.api_key})</span> : <span className="text-xs text-yellow-500">(brak)</span>}</Label>
                <div className="flex gap-2 mt-1">
                  <Input
                    type="password"
                    placeholder={p.api_key_set ? 'wpisz nowy żeby zmienić' : 'wpisz API key'}
                    onKeyDown={(e) => { if (e.key === 'Enter') { updateProvider(prov, { api_key: e.target.value, enabled: true }); e.target.value = ''; } }}
                  />
                </div>
                <p className="text-[10px] opacity-50 mt-1">Enter = zapisz. Dostępne modele: {info.models.join(', ')}</p>
              </div>
              <Button variant="outline" size="sm" onClick={() => setDefault(prov, info.models[0])} disabled={!p.enabled}>
                Ustaw jako default ({info.models[0]})
              </Button>
            </CardContent>
          </Card>
        );
      })}

      {/* TASKS */}
      <Card>
        <CardContent className="p-4 space-y-3">
          <div>
            <h3 className="font-semibold">Provider per zadanie</h3>
            <p className="text-xs text-muted-foreground">Możesz mieszać — np. intent_classifier na tanim modelu, message_generator na lepszym.</p>
          </div>
          {TASKS.map(task => {
            const t = cfg.tasks?.[task] || {};
            return (
              <div key={task} className="grid grid-cols-4 gap-2 items-center">
                <div className="font-mono text-xs">{task}</div>
                <select value={t.provider || cfg.default_provider} onChange={(e) => updateTask(task, { provider: e.target.value })} className="p-1 rounded border border-border bg-background text-sm">
                  {Object.keys(PROVIDER_INFO).map(p => <option key={p} value={p}>{p}</option>)}
                </select>
                <Input value={t.model || cfg.default_model} onChange={(e) => updateTask(task, { model: e.target.value })} placeholder="model" className="text-xs h-7" />
                <div className="text-xs opacity-60">domyślnie: {cfg.default_provider}/{cfg.default_model}</div>
              </div>
            );
          })}
        </CardContent>
      </Card>

      {/* TEST */}
      <Card>
        <CardContent className="p-4 space-y-3">
          <div>
            <h3 className="font-semibold">Test wywołania</h3>
            <p className="text-xs text-muted-foreground">Wyśle krótki prompt do wybranego task.</p>
          </div>
          <div className="flex gap-2 items-center">
            <select value={testTask} onChange={(e) => setTestTask(e.target.value)} className="p-2 rounded border border-border bg-background text-sm">
              {TASKS.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
            <Button onClick={runTest} disabled={testing}>{testing ? 'Testuję...' : 'Wyślij test'}</Button>
          </div>
          {testResult && (
            <div className={`p-3 rounded text-sm ${testResult.ok ? 'bg-green-500/10 border border-green-500/30' : 'bg-destructive/10 border border-destructive/30'}`}>
              {testResult.ok ? (
                <>
                  <div className="text-xs opacity-70 mb-1">✓ Odpowiedź:</div>
                  <div className="whitespace-pre-wrap font-mono text-xs">{testResult.response}</div>
                </>
              ) : (
                <>
                  <div className="text-xs opacity-70 mb-1">✗ Błąd:</div>
                  <div>{testResult.reason}</div>
                  {testResult.tried_provider && <div className="text-xs opacity-70 mt-1">Próbowany: {testResult.tried_provider}/{testResult.tried_model}</div>}
                </>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
